import { RiLockLine } from "@remixicon/react";
import type { LockedCard } from "@screener/protocol";
import { Card } from "@screener/uikit/components/card";
import { memo } from "react";
import { exchangeLabel, sideLabel } from "@/lib/labels";
import { CARD_EMPTY_TEXT_CLASS, CARD_HEADER_CLASS } from "./card-classes";
import { ExchangeLogo } from "./exchange-logo";
import { formatLockedRange } from "./locked-cards";
import { SideBadge } from "./side-badge";

interface LockedCardItemProps {
  locked: LockedCard;
  /** «Доступно на тарифе …» — текст считает вызывающий по составу тарифов (`PlanGate`). */
  text: string;
}

/**
 * Закрытая карточка тарифа (протокол 12): биржа, сторона, диапазон объёма и
 * замок с тарифом, который её открывает. Монеты, цены и дистанции у неё нет —
 * демон их не присылает. Действий открытой карточки тоже нет: она не
 * кликается, не закрепляется, нет избранного и заметок, поэтому ни кнопок, ни
 * точки табуляции здесь не бывает. Высота — как у карточки без строк
 * (`cardHeightPx(0, …)`): шапка и одна строка подписи.
 */
export const LockedCardItem = memo(function LockedCardItem({ locked, text }: LockedCardItemProps) {
  const range = formatLockedRange(locked.bucket_min_usd, locked.bucket_max_usd);
  return (
    <Card
      variant="lift"
      size="sm"
      data-slot="locked-card"
      role="group"
      aria-label={[exchangeLabel(locked.exchange), sideLabel(locked.side), range, text].join(", ")}
    >
      {/* Одно доступное имя — у группы; содержимое скрыто, чтобы диктор не читал его дважды. */}
      <div className={CARD_HEADER_CLASS} aria-hidden="true">
        <ExchangeLogo exchange={locked.exchange} className="h-3.5" />
        <SideBadge side={locked.side} flagged={false} />
        <span className="flex-1" />
        <span className="font-semibold text-[13px] whitespace-nowrap tabular-nums">{range}</span>
      </div>
      <p
        className={`${CARD_EMPTY_TEXT_CLASS} flex items-center gap-1.5 truncate`}
        aria-hidden="true"
      >
        <RiLockLine aria-hidden="true" className="size-3 shrink-0" />
        <span className="truncate">{text}</span>
      </p>
    </Card>
  );
});
